import styled from "@emotion/styled"
import React, { forwardRef, useRef, useState } from "react"
import Box, { BoxProps } from "@/components/Box"
import Typography from "@/components/Typography"
import { useOutsideAlerter } from "@/components/hooks/useOutsideAlerter"
import { CheckboxMenu } from "../CheckboxMenu"
import { DropDownMenu } from "./DropDownMenu"
import { ChevronDown, ChevronUp } from "@/components/Icon"

const Select = styled(Box)`
  appearance: none;
  border: none;
  background-color: transparent;
  -moz-appearance: none;
  -webkit-appearance: none;
  display: flex;
  align-items: center;
  position: relative;
  padding: 16px 14px;
  border: 2px solid #9ea1a2;
  border-radius: 8px;
  height: 52px;
  cursor: pointer;
`
const OpenMenuIcon = styled(ChevronDown)`
  position: absolute;
  right: 12px;
  pointer-events: none;
  font-size: 12px;
  opacity: 0.5;
`

const CloseMenuIcon = styled(ChevronUp)`
  position: absolute;
  right: 12px;
  pointer-events: none;
  font-size: 12px;
  opacity: 0.5;
`

interface ForwardedSelectProps extends BoxProps {
  variant?: "default" | "desktop"
  data: { [key: string]: string[] }
  filterArray: string[]
  allField: string
  label: string
  setFilter: (key: string[]) => void
}

const ForwardedSelect = forwardRef<HTMLAllCollection, ForwardedSelectProps>(
  (
    {
      variant = "default",
      width,
      data,
      filterArray,
      allField,
      label,
      setFilter,
      ...props
    },
    ref
  ) => {
    const [isMenuOpen, setMenuOpen] = useState(false)
    const wrapperRef = useRef(null)
    useOutsideAlerter(wrapperRef, setMenuOpen)
    return (
      <Box
        alignItems="center"
        display="inline-flex"
        width={width}
        position="relative"
        ref={wrapperRef}
        {...props}
      >
        <Select
          aria-label={label}
          variant={variant}
          onClick={() => setMenuOpen(!isMenuOpen)}
          ref={ref}
          width={width}
          as="button"
        >
          <Typography
            variant="p"
            fontSize={"16px"}
            fontWeight={600}
            lineHeight={"135%"}
            letterSpacing={"0.015em"}
            color={"#424242"}
          >
            {getSelectBoxContent(filterArray, data)}
          </Typography>
          {isMenuOpen ? <CloseMenuIcon /> : <OpenMenuIcon />}
        </Select>
        {isMenuOpen && (
          <DropDownMenu
            title={label}
            content={
              <CheckboxMenu
                data={data}
                filterArray={filterArray}
                setFilter={setFilter}
                allField={allField}
              />
            }
          />
        )}
      </Box>
    )
  }
)

const getSelectBoxContent = (
  activeFilter: string[],
  data: { [key: string]: string[] }
) => {
  if (!activeFilter.length) {
    return "Please Select Filter"
  } else if (activeFilter.length === 1) {
    return `${activeFilter[0]} (${data[activeFilter[0]].length})`
  } else {
    return `${activeFilter[0]} + ${activeFilter.length - 1}`
  }
}

ForwardedSelect.displayName = "Select"

export default ForwardedSelect
